import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import { withFirebase } from "../Firebase";
import { Board } from "../Board";
import { Header } from "../Header";

import "./Game.css";

const BOARD_WIDTH = 5;
const BOARD_HEIGHT = 4;
const N_CARDS = BOARD_WIDTH * BOARD_HEIGHT;

// TODO: define formula for number of player cards
const N_CARDS_RED = 7;
const N_CARDS_BLUE = 6;

const TEAMS = {
    "0": "red",
    "1": "blue",
    "2": "neutral",
    "3": "bomb",
};

const GIF_PACK_IDS = ["1", "2"];
const DEFAULT_PACK_ID = GIF_PACK_IDS[0];
const PACK_STORAGE_KEY = "preferredGifPack";

function shuffle(a) {
    /* https://stackoverflow.com/a/6274381/3403106 */

    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }

    return a;
}

function makeLayout() {
    const nCardsRed = N_CARDS_RED;
    const nCardsBlue = N_CARDS_BLUE;
    const nCardsBomb = 1;
    const nCardsNeutral = N_CARDS - nCardsRed - nCardsBlue - nCardsBomb;

    const reds = new Array(nCardsRed).fill("red");
    const blues = new Array(nCardsBlue).fill("blue");
    const bombs = new Array(nCardsBomb).fill("bomb");
    const neutrals = new Array(nCardsNeutral).fill("neutral");

    let layout = reds.concat(blues, bombs, neutrals);

    return shuffle(layout);
}

const getTeamFromInt = x => {
    return TEAMS[x];
};

const getIntFromTeam = team => {
    return Object.keys(TEAMS).find(key => TEAMS[key] === team);
};

const serializeBoardState = state => {
    let s = "";

    state.forEach(x => (x ? (s = s.concat("1")) : (s = s.concat("0"))));

    return s;
};

const deserializeBoardState = s => {
    let state = [];

    for (let i = 0; i < s.length; i++) {
        state.push(s[i] === "1" ? true : false);
    }

    return state;
};

const serializeLayout = layout => {
    let s = "";

    layout.forEach(x => (s = s.concat(getIntFromTeam(x))));

    return s;
};

const deserializeBoardLayout = s => {
    let layout = [];

    for (let i = 0; i < s.length; i++) {
        layout.push(getTeamFromInt(s[i]));
    }

    return layout;
};

const serializeGuessers = guessers => {
    let s = "";

    guessers.forEach(x => {
        if (x === "red") {
            s = s.concat("r");
        } else if (x === "blue") {
            s = s.concat("b");
        } else {
            s = s.concat("-");
        }
    });

    return s;
};

const deserializeGuessers = s => {
    const guessers = [];

    for (let i = 0; i < s.length; i++) {
        if (s[i] === "r") {
            guessers.push("red");
        } else if (s[i] === "b") {
            guessers.push("blue");
        } else {
            guessers.push(null);
        }
    }

    return guessers;
};

const getRandomSubarray = (arr, size) => {
    /* https://stackoverflow.com/a/11935263/3403106 */

    var shuffled = arr.slice(0),
        i = arr.length,
        temp,
        index;

    while (i--) {
        index = Math.floor((i + 1) * Math.random());
        temp = shuffled[index];
        shuffled[index] = shuffled[i];
        shuffled[i] = temp;
    }

    return shuffled.slice(0, size);
};

const buildGifIndexes = gifCount => {
    // Ensures we always have N_CARDS indexes, reusing gifs if the list is short.
    if (gifCount === 0) {
        return new Array(N_CARDS).fill(0);
    }

    const allGifIxs = [...Array(gifCount).keys()];
    let gifIxs = [];

    while (gifIxs.length < N_CARDS) {
        const remaining = N_CARDS - gifIxs.length;
        const nextBatch = getRandomSubarray(
            allGifIxs,
            Math.min(remaining, gifCount)
        );
        gifIxs = gifIxs.concat(nextBatch);
    }

    return gifIxs;
};

const getRandomStartingTeamIsRed = () => Math.random() < 0.5;

class Game extends Component {
    getStoredPackPreference() {
        try {
            return localStorage.getItem(PACK_STORAGE_KEY);
        } catch (e) {
            return null;
        }
    }

    setStoredPackPreference(packId) {
        try {
            localStorage.setItem(PACK_STORAGE_KEY, packId);
        } catch (e) {
            // ignore storage errors
        }
    }

    getPreferredPackId(gifPacks, fallback) {
        const available = Object.keys(gifPacks || {});
        if (!available.length) {
            return fallback || DEFAULT_PACK_ID;
        }
        const stored = this.getStoredPackPreference();
        const current = this.state && this.state.selectedPack;
        const pick = current || stored;
        if (pick && gifPacks[pick]) {
            return pick;
        }
        if (fallback && gifPacks[fallback]) {
            return fallback;
        }
        return available[0];
    }

    loadGifPacks() {
        const loadPack = packId =>
            fetch(`/gif-packs/${packId}.txt`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error("Failed to load gifs");
                    }
                    return response.text();
                })
                .then(text =>
                    text
                        .split("\n")
                        .map(line => line.trim())
                        .filter(Boolean)
                )
                .catch(error => {
                    console.error(`Failed to load GIF pack ${packId}`, error);
                    return new Array(N_CARDS).fill("");
                });

        return Promise.all(GIF_PACK_IDS.map(loadPack))
            .then(packs => {
                const gifPacks = {};
                packs.forEach((pack, idx) => {
                    const id = GIF_PACK_IDS[idx];
                    gifPacks[id] =
                        pack && pack.length > 0
                            ? pack
                            : new Array(N_CARDS).fill("");
                });

                const preferred = this.getPreferredPackId(
                    gifPacks,
                    DEFAULT_PACK_ID
                );

                this.setState({
                    gifPacks,
                    selectedPack: preferred,
                });

                return gifPacks;
            })
            .catch(error => {
                console.error("Failed to load GIF packs", error);
                const fallback = { [DEFAULT_PACK_ID]: new Array(N_CARDS).fill("") };
                this.setState({
                    gifPacks: fallback,
                    selectedPack: DEFAULT_PACK_ID,
                });
                return fallback;
            });
    }

    /**
     * The straightforward constructor
     * Game owns all game-related state. This includes:
     *      redRemainingGifs: The number of remaining gifs to victory for the red team
     *      blueRemainingGifs: The number of remaining gifs to victory for the blue team
     *      isRedTurn: Turn either belongs to red or blue
     *      isPlayer: Mode is either player or spymaster
     *      cellsClicked: Boolean array indicating if each cell has been clicked
     *      layout: String array representation of the board layout
     *      gifs: String array of gif urls
     */
    constructor(props) {
        super(props);

        this.state = {
            redRemainingGifs: N_CARDS_RED,
            blueRemainingGifs: N_CARDS_BLUE,
            isRedTurn: getRandomStartingTeamIsRed(),
            isPlayer: true,
            cellsClicked: Array(N_CARDS).fill(false),
            guessedBy: Array(N_CARDS).fill(null),
            layout: makeLayout(),
            gifPacks: {},
            selectedPack: DEFAULT_PACK_ID,
            currentPack: DEFAULT_PACK_ID,
            gifs: new Array(N_CARDS).fill(""),
            loading: false,
            gameEnded: false,
        };
    }

    componentDidMount() {
        this.setState({ loading: true });

        const { firebase, history } = this.props;
        const { pathname } = history.location;
        const boardRef = firebase.board(pathname);
        this.boardRef = boardRef;

        this.loadGifPacks()
            .then(gifPacks => {
                this.startBoardListener(boardRef, gifPacks);
            })
            .catch(() => {
                this.startBoardListener(boardRef, this.state.gifPacks);
            });
    }

    componentWillUnmount() {
        if (this.boardRef && this.boardListener) {
            this.boardRef.off("value", this.boardListener);
        }
    }

    startBoardListener(boardRef, loadedGifPacks) {
        const gifPacks = loadedGifPacks || this.state.gifPacks || {};
        const defaultPackId = this.getPreferredPackId(
            gifPacks,
            DEFAULT_PACK_ID
        );

        const getPackGifs = packId =>
            gifPacks[packId] ||
            gifPacks[defaultPackId] ||
            new Array(N_CARDS).fill("");

        this.boardListener = boardSnapshot => {
            const board = boardSnapshot.val();

            if (board) {
                const packId = board.gifPack || defaultPackId;
                const resolvedPackId = gifPacks[packId]
                    ? packId
                    : defaultPackId;
                const packGifs = getPackGifs(resolvedPackId);

                const gifs = board.gifs.map(g => packGifs[g] || "");

                const cellsClicked = deserializeBoardState(board.cellsClicked);

                const layout = deserializeBoardLayout(board.layout);

                const guessedBy = board.guessedBy
                    ? deserializeGuessers(board.guessedBy)
                    : Array(N_CARDS).fill(null);

                const isRedTurn = board.isRedTurn;

                this.setState({
                    gifs,
                    layout,
                    cellsClicked,
                    guessedBy,
                    isRedTurn,
                    redRemainingGifs: board.redRemainingGifs,
                    blueRemainingGifs: board.blueRemainingGifs,
                    gifPacks,
                    selectedPack: this.getPreferredPackId(
                        gifPacks,
                        resolvedPackId
                    ),
                    currentPack: resolvedPackId,
                    loading: false,
                });
            } else {
                const { cellsClicked, layout, isRedTurn, guessedBy } =
                    this.state;

                const packId = this.getPreferredPackId(
                    gifPacks,
                    defaultPackId
                );
                const packGifs = getPackGifs(packId);

                const gifIxs = buildGifIndexes(packGifs.length);

                const newBoard = {
                    isRedTurn,
                    cellsClicked: serializeBoardState(cellsClicked),
                    guessedBy: serializeGuessers(guessedBy),
                    layout: serializeLayout(layout),
                    redRemainingGifs: this.state.redRemainingGifs,
                    blueRemainingGifs: this.state.blueRemainingGifs,
                    gifs: gifIxs,
                    gifPack: packId,
                };

                this.setState({
                    gifs: gifIxs.map(i => packGifs[i] || ""),
                    gifPacks,
                    selectedPack: packId,
                    currentPack: packId,
                    loading: false,
                });

                boardRef.update(newBoard);
            }
        };

        boardRef.on("value", this.boardListener);
    }

    /** The handler for a clicked cell in player mode, updating cell visibility and current score */
    revealCell(cellId) {
        if (!this.state.isPlayer) {
            return;
        }

        const {
            cellsClicked,
            layout,
            redRemainingGifs,
            blueRemainingGifs,
            gameEnded,
            isRedTurn,
            guessedBy,
        } = this.state;

        const { firebase, history } = this.props;
        const { pathname } = history.location;

        // If this is the first click on this cell
        if (!gameEnded && !cellsClicked[cellId]) {
            // Update current game scoreboard
            if (layout[cellId] === "red") {
                const updates = {
                    redRemainingGifs: redRemainingGifs - 1,
                };

                this.setState(updates);

                firebase.board(pathname).update(updates);
            } else if (layout[cellId] === "blue") {
                const updates = {
                    blueRemainingGifs: blueRemainingGifs - 1,
                };

                firebase.board(pathname).update(updates);
            }

            // Update cell visibility
            const cellStatuses = cellsClicked.slice();
            const guessedByStatuses = guessedBy.slice();
            cellStatuses[cellId] = true;
            guessedByStatuses[cellId] = isRedTurn ? "red" : "blue";
            this.setState({
                cellsClicked: cellStatuses,
                guessedBy: guessedByStatuses,
            });

            const updates = {
                cellsClicked: serializeBoardState(cellStatuses),
                guessedBy: serializeGuessers(guessedByStatuses),
            };

            firebase.board(pathname).update(updates);
        }

        // End the game if cell is bomb
        if (layout[cellId] === "bomb") {
            // Mark the game as over
            this.setState({ gameEnded: true });

            // Update score
            if (isRedTurn) {
                const updates = {
                    blueRemainingGifs: -1,
                    redRemainingGifs: -99,
                };

                this.setState(updates);

                firebase.board(pathname).update(updates);
            } else {
                const updates = {
                    blueRemainingGifs: -99,
                    redRemainingGifs: -1,
                };

                this.setState(updates);

                firebase.board(pathname).update(updates);
            }
        }
    }

    /** Toggles the mode between player and spymaster */
    switchPlayerMode() {
        this.setState({ isPlayer: !this.state.isPlayer });
    }

    /** Passes the turn to the other team */
    endPlayerTurn() {
        const { gameEnded, isRedTurn } = this.state;
        const { firebase, history } = this.props;
        const { pathname } = history.location;

        if (!gameEnded) {
            const updates = { isRedTurn: !isRedTurn };

            this.setState(updates);
            firebase.board(pathname).update(updates);
        }
    }

    /** Initializes a new game */
    async newGame(packIdOverride) {
        const { pathname } = this.props.history.location;

        this.setState({ loading: true });

        let gifPacks = this.state.gifPacks;
        try {
            gifPacks = await this.loadGifPacks();
        } catch (e) {
            console.error("Failed to refresh GIF packs", e);
            gifPacks = this.state.gifPacks;
        }

        const packId =
            packIdOverride ||
            (this.state.selectedPack && gifPacks[this.state.selectedPack]
                ? this.state.selectedPack
                : this.getPreferredPackId(gifPacks, DEFAULT_PACK_ID));
        const packGifs =
            gifPacks[packId] || new Array(N_CARDS).fill("");
        this.setStoredPackPreference(packId);

        const gifIxs = buildGifIndexes(packGifs.length);

        const newCellsClicked = Array(N_CARDS).fill(false);
        const newGuessedBy = Array(N_CARDS).fill(null);
        const newLayout = makeLayout();
        const isRedTurn = getRandomStartingTeamIsRed();

        const newBoard = {
            gifs: gifIxs,
            isRedTurn,
            cellsClicked: serializeBoardState(newCellsClicked),
            guessedBy: serializeGuessers(newGuessedBy),
            layout: serializeLayout(newLayout),
            redRemainingGifs: N_CARDS_RED,
            blueRemainingGifs: N_CARDS_BLUE,
            gifPack: packId,
        };

        this.setState({
            gifs: gifIxs.map(i => packGifs[i] || ""),
            redRemainingGifs: N_CARDS_RED,
            blueRemainingGifs: N_CARDS_BLUE,
            isRedTurn,
            isPlayer: true,
            cellsClicked: newCellsClicked,
            guessedBy: newGuessedBy,
            layout: newLayout,
            loading: false,
            gameEnded: false,
            selectedPack: packId,
            currentPack: packId,
        });

        this.props.firebase.board(pathname).update(newBoard);
    }

    changePack(packId) {
        this.setStoredPackPreference(packId);
        this.setState({ selectedPack: packId });
    }

    render() {
        const { gifs, loading } = this.state;

        return (
            <div className="game">
                <Header
                    redRemainingGifs={this.state.redRemainingGifs}
                    blueRemainingGifs={this.state.blueRemainingGifs}
                    isRedTurn={this.state.isRedTurn}
                    isPlayer={this.state.isPlayer}
                    switchPlayerMode={() => {
                        this.switchPlayerMode();
                    }}
                    endPlayerTurn={() => {
                        this.endPlayerTurn();
                    }}
                    gameEnded={this.state.gameEnded}
                    newGame={packId => {
                        this.newGame(packId);
                    }}
                    selectedPack={this.state.selectedPack}
                    currentPack={this.state.currentPack}
                    packOptions={Object.keys(this.state.gifPacks)}
                    onPackChange={packId => {
                        this.changePack(packId);
                    }}
                />

                {loading && <div>Loading...</div>}

                <Board
                    isPlayer={this.state.isPlayer}
                    cellsClicked={this.state.cellsClicked}
                    guessedBy={this.state.guessedBy}
                    layout={this.state.layout}
                    gifs={gifs}
                    revealCell={i => {
                        this.revealCell(i);
                    }}
                />
            </div>
        );
    }
}

export default withRouter(withFirebase(Game));
